import React from "react";
import { Helmet } from "react-helmet";
import { Lock, Cpu, Shield, FileText } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/wallet-password-recovery" className="dark-nav-link active">PASSWORD RECOVERY</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const WalletPasswordRecoveryPage = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Wallet Password Recovery | Owner-Authorized (CPU/GPU) | ARCANA CRYPTO</title>
        <meta name="description" content="Professional wallet password recovery for wallet.dat, keystore JSON, and encrypted wallets. Owner-authorized, CPU/GPU accelerated. Non-custodial." />
        <link rel="canonical" href="https://arcana-crypto.com/wallet-password-recovery-guide.html" />
      </Helmet>
      <Navigation />
      
      <section className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title" style={{fontSize: '48px'}}>
            Wallet Password Recovery Service
          </h1>
          <p className="section-subtitle">
            Professional Password Recovery for Encrypted Wallets (Owner-Authorized)
          </p>

          <div className="section-intro" style={{marginTop: '40px'}}>
            <p style={{fontSize: '20px'}}>
              Forgotten wallet passwords are a solvable problem when you have partial information. 
              Arcana Crypto provides owner-authorized password recovery services using controlled 
              computational methodologies, pattern analysis, and GPU-accelerated techniques.
            </p>
          </div>

          {/* What We Recover */}
          <div style={{marginTop: '80px'}}>
            <h2 className="section-title">Wallet Types We Support</h2>
            
            <div className="card-grid" style={{marginTop: '60px'}}>
              <div className="card">
                <Lock className="card-icon" />
                <h3 className="card-title">ENCRYPTED WALLETS</h3>
                <ul className="card-list">
                  <li>Bitcoin Core (wallet.dat)</li>
                  <li>Ethereum keystore (JSON files)</li>
                  <li>Litecoin/Dogecoin wallet files</li>
                  <li>Electrum encrypted wallets</li>
                </ul>
                <div className="card-price">£600 - £1,200</div>
              </div>

              <div className="card">
                <Cpu className="card-icon" />
                <h3 className="card-title">RECOVERY METHODS</h3>
                <ul className="card-list">
                  <li>Pattern-based recovery (partial memory)</li>
                  <li>Dictionary attacks (custom wordlists)</li>
                  <li>GPU-accelerated computation</li>
                  <li>Hybrid approaches for complex cases</li>
                </ul>
                <div className="card-price">Success: Variable</div>
              </div>

              <div className="card">
                <Shield className="card-icon" />
                <h3 className="card-title">SECURITY GUARANTEE</h3>
                <ul className="card-list">
                  <li>Owner authorization required</li>
                  <li>Non-custodial process</li>
                  <li>Encrypted file handling</li>
                  <li>Secure password delivery</li>
                </ul>
                <div className="card-price">100% Confidential</div>
              </div>

              <div className="card">
                <FileText className="card-icon" />
                <h3 className="card-title">WHAT WE NEED</h3>
                <ul className="card-list">
                  <li>Encrypted wallet file</li>
                  <li>Partial password memory (if any)</li>
                  <li>Password pattern/rules (length, chars)</li>
                  <li>Verification address (proof of ownership)</li>
                </ul>
                <div className="card-price">Free Assessment</div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">Password Recovery Pricing</h2>
            
            <div className="pricing-grid" style={{marginTop: '60px'}}>
              <div className="pricing-tier">
                <div className="tier-name">STANDARD</div>
                <h3 className="tier-title">CPU-Based Recovery</h3>
                <div className="tier-price">£600</div>
                <div className="tier-divider"></div>
                <ul className="tier-features">
                  <li>Shorter passwords (6-10 chars)</li>
                  <li>Pattern-based with strong clues</li>
                  <li>Dictionary-based recovery</li>
                  <li>Time: Hours to days</li>
                </ul>
                <div className="tier-meta">Best for: Partial memory cases</div>
              </div>

              <div className="pricing-tier" style={{borderColor: 'var(--oro-arcana)'}}>
                <div className="tier-name" style={{background: 'var(--oro-arcana)'}}>ADVANCED</div>
                <h3 className="tier-title">GPU-Accelerated Recovery</h3>
                <div className="tier-price">£1,200</div>
                <div className="tier-divider"></div>
                <ul className="tier-features">
                  <li>Complex passwords (10-15 chars)</li>
                  <li>Weak pattern memory</li>
                  <li>GPUs computation (faster)</li>
                  <li>Time: Hours to multiple days</li>
                </ul>
                <div className="tier-meta">Best for: Complex cases</div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">Password Recovery Process</h2>
            
            <div className="steps-grid" style={{marginTop: '60px'}}>
              <div className="step">
                <div className="step-number">01</div>
                <h3 className="step-title">SUBMIT WALLET FILE</h3>
                <p className="step-description">
                  Send encrypted wallet file securely (we never see unencrypted contents)
                </p>
              </div>

              <div className="step">
                <div className="step-number">02</div>
                <h3 className="step-title">PATTERN ANALYSIS</h3>
                <p className="step-description">
                  Provide any partial memory (length, characters used, patterns remembered)
                </p>
              </div>

              <div className="step">
                <div className="step-number">03</div>
                <h3 className="step-title">COMPUTATIONAL RECOVERY</h3>
                <p className="step-description">
                  We apply controlled methodologies (CPU or GPU-accelerated)
                </p>
              </div>

              <div className="step">
                <div className="step-number">04</div>
                <h3 className="step-title">PASSWORD DELIVERY</h3>
                <p className="step-description">
                  Upon success, password delivered encrypted - you gain wallet access
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Specific */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">Wallet Password Recovery FAQ</h2>
            
            <div className="faq-list" style={{marginTop: '60px'}}>
              <div className="faq-item">
                <h3 className="faq-question">What if I remember nothing about the password?</h3>
                <p className="faq-answer">
                  Feasibility is low but not zero. We perform a free assessment first. If computational 
                  requirements are extreme relative to wallet value, we'll advise honestly.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">Do you need access to my wallet funds?</h3>
                <p className="faq-answer">
                  No. We work with the encrypted wallet file only. Funds remain under your control. 
                  We deliver the password - you access the wallet yourself.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="faq-question">How long does password recovery take?</h3>
                <p className="faq-answer">
                  Depends on password complexity and clues provided. Simple cases: hours. Complex cases: 
                  days to weeks. Emergency priority available.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="cta-section" style={{marginTop: '100px'}}>
            <h2 className="cta-title">Forgotten Your Wallet Password?</h2>
            <p className="cta-subtitle">
              Start with a free technical assessment
            </p>
            <button className="btn-primary" style={{fontSize: '20px', padding: '20px 48px', marginTop: '32px'}} onClick={() => window.location.href = '/#assessment'}>
              FREE PASSWORD RECOVERY ASSESSMENT
            </button>
          </div>

        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WalletPasswordRecoveryPage;
